import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, type ReactNode } from "react";
import { useAuth } from "~/hooks/useAuth";

/**
 * Drops the previous account's cached queries when the signed-in user
 * changes, so a sign-out followed by a different sign-in never shows a flash
 * of the previous account's data.
 *
 * It removes only queries keyed by the *previous* user id. Removing every
 * authenticated query on any change (what this used to do) races with sign-in:
 * effects run child-first, so the counters provider had already started
 * fetching the new user's data by the time this effect ran, and deleting that
 * in-flight query left the UI stuck on "Loading…". Nothing needs clearing when
 * going from signed-out to signed-in — sign-out already cleared everything.
 * The guest cache is never keyed by a user id, so it is never touched.
 */
export function QuerySessionBoundary({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const lastUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const userId = user?.id ?? null;
    const previous = lastUserId.current;
    if (previous && previous !== userId) {
      queryClient.resetQueries({
        predicate: (query) => query.queryKey.includes(previous),
      });
    }
    lastUserId.current = userId;
  }, [queryClient, user?.id]);

  return children;
}
